/**
 * useTodoSample — 나의 할일 데이터 접근 훅(배너 · 나의 할일 · 현장 슬라이드 공용).
 * GET /schedule/todos(useTodos)를 조회해 화면 줄로 옮긴다. scope=week 면 7일 날짜 카드(weekDays)도 함께 만든다.
 * 서버 신호가 비면 503 SIGNAL_MISSING 이 오류로 올라오며 isError 로 명시적 오류 상태를 보여준다 — 샘플·로컬 값으로 대체하지 않는다.
 * 훅 이름·반환 형태(todos·week·isLoading·isError)는 유지한다.
 */
import { useMemo } from 'react';
import { useCompanyMembers } from '@indipia/shared-features';
import { toTodoItem, toWeekDays, type TodoItem, type WeekDayView } from './todoModel';
import { useTodos, type UseTodosOptions } from './useTodos';

export interface TodoSampleResult {
  /** 오늘 할일 — 서버 정렬 그대로 */
  todos: TodoItem[];
  /** 7일 날짜 카드 — scope=week 일 때만, 아니면 빈 배열 */
  week: WeekDayView[];
  /** 7일 일정 수(서버 summary.weekCount) */
  weekCount: number;
  isLoading: boolean;
  /** 조회 오류(SIGNAL_MISSING 포함) */
  isError: boolean;
  /** 조회 오류 원인 — SignalMissingError 여부 판별용 */
  error: unknown;
  /** 조회 시각(ms, 조회 전 0) */
  fetchedAt: number;
  /** 다시 조회(오류 상태의 [다시 시도]) */
  refetch: () => unknown;
}

export function useTodoSample(opts: UseTodosOptions = {}): TodoSampleResult {
  const q = useTodos(opts);
  // 회원 이름은 표시 보조 — 실패해도 이름 없이 보이므로 오류로 올리지 않는다.
  const { data: members } = useCompanyMembers();

  const todos = useMemo(() => {
    const memberNames = new Map((members ?? []).map((m) => [m.memberId, m.memberName] as const));
    return (q.data?.items ?? []).map((t) => toTodoItem(t, { memberNames }));
  }, [q.data, members]);

  const week = useMemo(() => toWeekDays(q.data?.weekDays, todos), [q.data, todos]);

  return {
    todos,
    week,
    weekCount: q.data?.summary.weekCount ?? 0,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
    fetchedAt: q.dataUpdatedAt,
    refetch: q.refetch,
  };
}
